import React, { useCallback, useReducer, useState } from 'react';
import { useHistory } from 'react-router-dom';
import cx from 'classnames';
import PropTypes from 'prop-types';

import {
  actions,
  BetTypeProvider,
  HistoryManager,
  IntlManager,
  NotificationManager,
  useApplicationState,
  useBetslipState,
} from '@atlas/core';
import { staticRoutes } from '@atlas/routing';
import {
  BetslipErrors,
  Button,
  Icon,
  Label,
  MoneyInput,
  OddsInput,
  ToggleCheckbox,
} from '@atlas/ui';

import { ACCOUNT_STATUSES } from 'common/constants';
import { useAccountBlocked } from 'hooks/useAccountBlocked';
import { useSelfRegulationContext } from 'src/components/SelfRegulationProvider';

import { useLocalBetslip } from './LocalBetslipContext';

const ORDER_ODDS_INPUT_RX = /^\d+\.?\d{0,2}$/m;

const OrderBetslipControlsWithProvider = ({ currency }) => {
  const { betItems, betTypes } = useBetslipState();
  const selectedBet = betTypes[0] || betItems[0] || {};

  return (
    <BetTypeProvider betType={selectedBet} key={selectedBet.id}>
      <OrderBetslipControls currency={currency} />
    </BetTypeProvider>
  );
};

const createOrderInitialState = bet => ({
  acceptBetAmountChange: false,
  acceptSportServiceChange: false,
  betAmount: '',
  minDesiredOdds: Math.round(bet.odds * 100) / 100 + 0.01,
});

const orderReducer = (state, action) => {
  if (action.type === actions.STATE_UPDATE) {
    return {
      ...state,
      ...action.payload,
    };
  }

  return state;
};

const OrderBetslipControls = ({ currency }) => {
  const { betItems, betTypes, loading } = useBetslipState();
  const { account } = useApplicationState();
  const history = useHistory();
  const [active, setActive] = useState(false);
  const [activeOdds, setActiveOdds] = useState(false);
  const { setIsOpenPopup } = useLocalBetslip();
  const accountBlocked = useAccountBlocked();
  const selectedBet = betTypes[0] || betItems[0] || {};
  const [state, dispatch] = useReducer(orderReducer, selectedBet, createOrderInitialState);
  const { acceptBetAmountChange, acceptSportServiceChange, betAmount, minDesiredOdds } = state;
  const { showSelfRegulationPopupIfNeeded, showSpentLimitPopupIfNeeded } =
    useSelfRegulationContext();

  const onOrderBet = async () => {
    if (account.status === ACCOUNT_STATUSES.PARTIALLY_REGISTERED) {
      setIsOpenPopup(false);
      history.push(staticRoutes.signup);
      return;
    }

    if (accountBlocked) {
      setIsOpenPopup(false);
      NotificationManager.display({
        id: 'accountBlocked',
        type: 'accountBlocked',
      });
      return;
    }

    const canProceedSpentLimit = await showSpentLimitPopupIfNeeded();
    if (!canProceedSpentLimit) return;

    const canProceedSelfRegulation = await showSelfRegulationPopupIfNeeded();
    if (!canProceedSelfRegulation) return;

    // Switching betslip loading
    // Cleaning errors
    HistoryManager.submit(HistoryManager.actions.operation.order, {
      acceptBetAmountChange,
      acceptSportServiceChange,
      betAmount,
      betCurrency: currency,
      betItems,
      minDesiredOdds,
      skipInactiveOutcomes: false,
    }).catch(error => {
      NotificationManager.notify({
        message: `order_bet_error_${error.code}`,
        status: 'error',
        type: 'notification',
      });
    });
  };

  const onOddsInputChangeHandler = React.useCallback(minDesiredOdds => {
    if (
      minDesiredOdds === '' || // On input clearing
      ORDER_ODDS_INPUT_RX.test(minDesiredOdds)
    ) {
      dispatch({
        payload: { minDesiredOdds },
        type: actions.STATE_UPDATE,
      });
    }
  }, []);

  const setOrderAmount = React.useCallback(betAmount => {
    dispatch({
      payload: { betAmount },
      type: actions.STATE_UPDATE,
    });
  }, []);

  const switchAcceptSportServiceChange = React.useCallback(() => {
    const value = !acceptSportServiceChange;

    dispatch({
      payload: { acceptSportServiceChange: value },
      type: actions.STATE_UPDATE,
    });
  }, [acceptSportServiceChange]);

  const switchAcceptBetAmountChange = React.useCallback(() => {
    const value = !acceptBetAmountChange;

    dispatch({
      payload: { acceptBetAmountChange: value },
      type: actions.STATE_UPDATE,
    });
  }, [acceptBetAmountChange]);

  const onKeyUp = useCallback(({ currentTarget, which }) => {
    // 13 is enter
    if (which === 13) {
      currentTarget.blur();
    }
  }, []);

  const isOddsValid = minDesiredOdds > selectedBet.odds;
  const isValid = betAmount && selectedBet && minDesiredOdds && isOddsValid;

  const toggleTurnLiveOrderBetClass = cx('toggle-container', { active: acceptSportServiceChange });
  const toggleAcceptBetAmountClass = cx('toggle-container', { active: acceptBetAmountChange });

  const fieldClassName = cx(
    'desired-odds',
    loading && 'field-disabled',
    !isOddsValid && 'field-error',
    'has-value',
  );

  return (
    <div className="betslip-classic-controls">
      <BetslipErrors />

      <div className="quick-bet-container order-bet">
        <div className={toggleTurnLiveOrderBetClass}>
          <ToggleCheckbox
            checked={acceptSportServiceChange}
            disabled={loading}
            id="betslip-live-order-bet"
            label="turn_live_order_bet_on"
            onChange={switchAcceptSportServiceChange}
          />
        </div>
        <div className={toggleAcceptBetAmountClass}>
          <ToggleCheckbox
            checked={acceptBetAmountChange}
            disabled={loading}
            id="betslip-bet-amount-change"
            label="accept_bet_amount_change"
            onChange={switchAcceptBetAmountChange}
          />
        </div>
      </div>

      <div className="order-bet-amount-input">
        <MoneyInput
          active={active}
          currency={currency}
          dataTestId="betslip-amount"
          disabled={loading}
          label="Amount"
          onBlur={() => setActive(false)}
          onChange={setOrderAmount}
          onFocus={() => setActive(true)}
          value={betAmount}
        />
      </div>
      <div className="total-bet-info bet-info-container">
        <div className="title-small-text">
          <Label message="possible_win" />:
          <span className="potential-win-amount">
            <span>{isValid ? IntlManager.formatMoney(betAmount * minDesiredOdds) : 0}</span>
          </span>
        </div>
      </div>
      {!isOddsValid && (
        <div className="notification-item notification-item__error">
          <div className="notification-item-icon">
            <Icon className="icon-error" name="error" />
          </div>
          <span className="notification-item-text">
            <Label className="main-label" message="order_bet_wrong_odds_error" />
          </span>
        </div>
      )}
      <div className="order-bet-controls-container">
        <OddsInput
          active={activeOdds}
          className={fieldClassName}
          disabled={loading}
          error={!isOddsValid}
          label="desired_odds"
          onBlur={() => setActiveOdds(false)}
          onChange={onOddsInputChangeHandler}
          onFocus={() => setActiveOdds(true)}
          onKeyUp={onKeyUp}
          value={minDesiredOdds}
        />

        <Button
          block
          disabled={loading || !isValid}
          label="place_order_bet"
          loading={loading}
          onClick={onOrderBet}
          size="big"
          type="primary"
        />
      </div>
    </div>
  );
};

OrderBetslipControls.propTypes = { currency: PropTypes.string.isRequired };

OrderBetslipControlsWithProvider.propTypes = { currency: PropTypes.string.isRequired };

export default OrderBetslipControlsWithProvider;
